import {DataTable} from "../DataTable/DataTable";
import {DispatchRequestEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {Anchor, Group, Stack} from "@mantine/core";
import {Link} from "react-router-dom";
import {
  DispatchRequestProgress,
  fromDispatchRequest
} from "../../progress/DispatchRequestProgress/DispatchRequestProgress";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {IconCircleCheckFilled} from "@tabler/icons-react";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";

const EMPTY_ARRAY: DispatchRequestEntity[] = []

export type DispatchRequestDataTableProps = EntityDataTableProps<DispatchRequestEntity>

export const DispatchRequestDataTable = (props: DispatchRequestDataTableProps) => {
  const {
    records: requests = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: requests,
    sortStatus: {columnAccessor: 'scheduledDttm', direction: 'desc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: t("table.entity.dispatchrequest.column.id", "ID"),
          accessor: "resourceId",
          width: 100,
          render: ({id, resourceId, isAuthorized}) => (
            <Anchor component={Link} to={`/requests/profile?id=${id}`}>
              <Group spacing="xs">
                {!isAuthorized && <IconCircleCheckFilled size={12}/>}
                <DefaultText value={resourceId}/>
              </Group>
            </Anchor>
          ),
        },
        {
          title: t("table.entity.dispatchrequest.column.status", "Status"),
          accessor: "status",
          width: 100,
          render: (dispatchRequest) => (
            <Stack>
              {t(dispatchRequest.status.displayValue.id, dispatchRequest.status.displayValue.defaultMessage)}
              <DispatchRequestProgress size={12} {...fromDispatchRequest(dispatchRequest)} />
            </Stack>
          ),
        },
        {
          title: t("table.entity.dispatchrequest.column.scheduleddate", "Scheduled Date"),
          accessor: "scheduledDttm",
          width: 100,
          sortable: true,
          render: ({scheduledDttm}) => (
            <FormattedDateTime date={scheduledDttm}/>
          )
        },
        {
          title: t("table.entity.dispatchrequest.column.pickup", "Pickup"),
          accessor: "pickupLocation",
          width: 100,
          sortable: true,
        },
        {
          title: t("table.entity.dispatchrequest.column.delivery", "Delivery"),
          accessor: "deliveryLocation",
          width: 100,
          sortable: true,
        },
        {
          title: t("table.entity.dispatchrequest.column.quantity", "Quantity"),
          accessor: "quantityDisplay",
          sortable: true,
          width: 100,
        },
        {
          title: t("table.entity.dispatchrequest.column.freighttype", "Freight Type"),
          accessor: "freightTypeName",
          width: 100,
          sortable: true,
          render: ({freightTypeId, freightTypeName}) => (
            freightTypeId ? <DefaultText value={freightTypeName}/> : ""
          )
        },
        {
          title: t("table.entity.dispatchrequest.column.servicetype", "Service Type"),
          accessor: "serviceTypeName",
          width: 100,
          sortable: true,
          render: ({serviceTypeId, serviceTypeName}) => (
            serviceTypeId ? <DefaultText value={serviceTypeName}/> : ""
          )
        },
        {
          title: t("table.entity.dispatchrequest.column.truckcompany", "Truck Company"),
          accessor: "truckingCompanyName",
          width: 100,
          sortable: true,
          render: ({truckingCompanyId, truckingCompanyName}) => (
            truckingCompanyId ? <DefaultText value={truckingCompanyName}/> : ""
          )
        },
      ]}
    />
  );
};
